import { memo, useMemo } from "react";

import { useProduct } from "@shopify/hydrogen-react";
import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { type BadgeType } from "@components/Badge";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  type CartMediaImage,
  type ProductHeroSettings,
} from "@root/tablescape/state";
import {
  formatProductVariant,
  formatFullyQualifiedUrl,
} from "@root/tablescape/utilities";

import { ProductGalleryCarouselView } from "./ProductGalleryCarousel.view";

const ProductGalleryCarouselBase = () => {
  const { product, selectedVariant } = useProduct();
  const {
    productGalleryCarousel: { lcpMedia },
    ugcVideoCarousel: {
      text: { playVideoModal: videoLabel },
    },
  } = useSectionSettings<ProductHeroSettings>();

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const formattedAlternateImage = useMemo(() => {
    if (formattedSelectedVariant.alternateImage) {
      return {
        alt: formattedSelectedVariant.alternateImage.alt,
        src: formatFullyQualifiedUrl(
          formattedSelectedVariant.alternateImage.image.url
        ),
      };
    }
  }, [formattedSelectedVariant]);

  const medias = useMemo(
    () => formattedSelectedVariant.media,
    [formattedSelectedVariant]
  );

  const resolvedBadgeType = useMemo<Maybe<BadgeType>>(() => {
    switch (formattedSelectedVariant.badgeType) {
      case "sage":
        return "tertiary";
      case "brick":
        return "quaternary";
      case "bright-blue":
        return "senary";
      default:
        return "primary";
    }
  }, [formattedSelectedVariant]);

  const videoAriaLabel = useMemo(
    () => `${videoLabel} ${product.title}`,
    [product]
  );

  const isLCPRender = useMemo(() => {
    const firstImageMedia = medias.find(
      (media): media is CartMediaImage => media.mediaContentType === "IMAGE"
    );

    if (!firstImageMedia || !lcpMedia.sources?.length) {
      return false;
    }

    const url = new URL(firstImageMedia.image.url);
    const path = url.pathname.split("/").reverse()[0];

    return lcpMedia.sources[0].src.includes(path);
  }, [lcpMedia, medias]);

  return (
    <ProductGalleryCarouselView
      alternateImage={formattedAlternateImage}
      badgeText={formattedSelectedVariant.badgeText}
      badgeType={resolvedBadgeType}
      isLCPRender={isLCPRender}
      lcpMedia={lcpMedia}
      medias={medias}
      videoAriaLabel={videoAriaLabel}
    />
  );
};

export const ProductGalleryCarousel = memo(ProductGalleryCarouselBase);
